import { useState } from 'react'
import routes from 'router/routes'
import { useNavigate } from 'react-router-dom'

import Divider from '@mui/material/Divider'
import {
  Button,
  DividedContainer,
  Loader,
  Typography,
  LoaderColorE,
  ImagePreviewContainer,
  ConnectWalletDialog,
} from 'primsell-shared-layer/components'
import { getFormattedAmount } from 'primsell-shared-layer/helpers'
import { BuyForm, StripeSessionTimer, IsNotStartedTimer, InfoMessage } from './components'
import { StripeComponent } from 'components'

import { usePaymentController } from './payment.controller'

import { BadgeChecked, ExclamationIcon } from 'primsell-shared-layer/assets/icons'
import * as S from './payment.styles'

const Payment = (): JSX.Element => {
  const navigate = useNavigate()
  const [isOpenConnectDialog, setIsOpenConnectDialog] = useState<boolean>(false)

  const {
    info,
    amount,
    publicCampaignDetails,
    walletAddress,
    isLoadingCampaignPublic,
    mediumPublicCampaignImage,
    originalPublicCampaignImage,
    agreeCheck,
    totalPrice,
    formikExternalRef,
    isPaymentSessionExpired,
    isConnectButtonDisabled,
    setAmount,
    setAgreeCheck,
    handleDisconnect,
    handleRenewPaymentSession,
    setIsPaymentSessionExpired,
    handleConnectWalletSuccess,
  } = usePaymentController()

  if (!publicCampaignDetails) {
    if (isLoadingCampaignPublic) {
      return (
        <S.LoadingContainer>
          <Loader color={LoaderColorE.DARK} />
        </S.LoadingContainer>
      )
    } else navigate(routes.ROUTE_NOT_FOUND)
  }

  return (
    <S.BuyFormPaymentWrapper>
      <S.TimerContainer>
        <StripeSessionTimer setIsPaymentSessionExpired={setIsPaymentSessionExpired} />
        <IsNotStartedTimer />
      </S.TimerContainer>
      <DividedContainer
        left={
          <S.Content>
            {publicCampaignDetails && publicCampaignDetails.price !== null && (
              <>
                <ImagePreviewContainer
                  previewSrc={mediumPublicCampaignImage}
                  modalSrc={originalPublicCampaignImage}
                  alt="nft-preview"
                />
                <S.NFTInfo>
                  <S.NFTTitleTypography>{publicCampaignDetails.name}</S.NFTTitleTypography>
                  <Typography fontWeight="bold">
                    {getFormattedAmount(publicCampaignDetails.price, publicCampaignDetails.currency)}{' '}
                    {publicCampaignDetails.currency}
                  </Typography>
                </S.NFTInfo>
                <S.InStockTypography color="grey">
                  {publicCampaignDetails.inStock ? `In stock: ${publicCampaignDetails.inStock}` : 'Sold out'}
                </S.InStockTypography>
                <Divider />
                <S.TotalPay>
                  <S.TotalPayTypography color="grey">Total to Pay</S.TotalPayTypography>
                  <Typography variant="h2">
                    {totalPrice ? getFormattedAmount(totalPrice, publicCampaignDetails.currency) : '0.00'}{' '}
                    {publicCampaignDetails.currency}
                  </Typography>
                </S.TotalPay>
              </>
            )}
          </S.Content>
        }
        right={
          <S.Content>
            <>
              <S.TitleTypography variant="h3">Payment</S.TitleTypography>
              {!walletAddress ? (
                <>
                  <S.DescTypography variant="h5">
                    To start the payment process, please, connect your wallet.
                  </S.DescTypography>
                  <Button
                    color="dark"
                    variant="filled"
                    onClick={() => setIsOpenConnectDialog(true)}
                    disabled={isConnectButtonDisabled}
                  >
                    Connect wallet
                  </Button>
                  <S.InfoBlock>
                    <ExclamationIcon />
                    <S.Link to="https://www.youtube.com/watch?v=eYpz34noK6Y" target="_blank">
                      Learn how to install Metamask Wallet
                    </S.Link>
                  </S.InfoBlock>
                </>
              ) : (
                <>
                  <S.WalletData>
                    <S.WalletLabel>
                      <BadgeChecked />
                      <S.Typography>Wallet connected</S.Typography>
                    </S.WalletLabel>
                    <S.Typography>{`${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`}</S.Typography>
                    <S.Button variant="outlined" color="grey" onClick={handleDisconnect}>
                      Disconnect
                    </S.Button>
                  </S.WalletData>
                </>
              )}
            </>
            <Divider />
            {info ? (
              <InfoMessage />
            ) : (
              <BuyForm
                amount={amount}
                setAmount={setAmount}
                ref={formikExternalRef}
                isPaymentSessionExpired={isPaymentSessionExpired}
                handleRenewPaymentSession={handleRenewPaymentSession}
              />
            )}
            {walletAddress && !isPaymentSessionExpired && (
              <StripeComponent agreeCheck={agreeCheck} setAgreeCheck={setAgreeCheck} />
            )}
          </S.Content>
        }
      />
      <ConnectWalletDialog
        open={isOpenConnectDialog}
        handleClose={() => setIsOpenConnectDialog(false)}
        onSuccessConnect={handleConnectWalletSuccess}
      />
    </S.BuyFormPaymentWrapper>
  )
}

export { Payment }
